<script>
import { cloneDeep } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import ItineraryPricesPriceSettingsHotelModal from './ItineraryPricesPriceSettingsHotelModal';
import ItineraryPricesPriceSettingsSupplierModal from './ItineraryPricesPriceSettingsSupplierModal';
import ItineraryPricesPriceSettingsTableRow from './ItineraryPricesPriceSettingsTableRow';
import ItineraryPricesTransportSearchBar from './ItineraryPricesTransportSearchBar';
import ItineraryPricesTableHeaders from '../../../variables/itineraryPrices/itineraryPricesTableHeader';
import ItineraryPricesDefaultObjects from '../../../variables/itineraryPrices/itineraryPricesDefaultObjects';
import ItineraryPricesPriceSelector from './ItineraryPricesPriceSelector';
import ItineraryPricesCategories from '../../../variables/itineraryPrices/itineraryPricesCategories';
import ItineraryPricesPriceSettingTableActions from './ItineraryPricesPriceSettingTableActions';
import ItineraryPricesTransportSearchFeed from './ItineraryPricesTransportSearchFeed';
import NonDynamicTransportAllotmentRow from '../../non-dynamic-transport/NonDynamicTransportAllotmentRow';

export default {
  name: 'ItineraryPricesPriceSettingsTable',
  components: {
    ItineraryPricesPriceSettingsHotelModal,
    ItineraryPricesPriceSettingsSupplierModal,
    ItineraryPricesPriceSettingsTableRow,
    ItineraryPricesTransportSearchBar,
    ItineraryPricesPriceSelector,
    ItineraryPricesPriceSettingTableActions,
    ItineraryPricesTransportSearchFeed,
    NonDynamicTransportAllotmentRow,
  },
  props: {
    selectedPriceInterval: {
      type: Object,
      required: true,
    },
    priceIntervalSettings: {
      type: Object,
      default: () => {},
    },
    paxHeaders: {
      type: Array,
      required: false,
    },
    dayProgram: {
      type: Array,
      required: true,
    },
    startDate: {
      type: String,
      default: null,
    },
    ageDiscountPaxes: {
      type: Array,
      default: () => [],
    },
    transits: {
      type: Array,
      required: true,
    },
    carriers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      mergedHeaders: [],
      localPriceSettings: [],
      localGroupPaxes: [],
      localDiscountPricePaxes: [],
      localPackagePrices: [],
      localGrossPrice: null,
      ageDiscountHeaders: [],
      ageDiscountModal: false,
      selectedAgeDiscount: 0,
      mergedPackageHeaders: [],
      defaultPrices: null,
      validParticipantsAmounts: [],
      priceSettingSnapShot: null,
      priceSettingGrossPriceSnapShot: null,
      transportAllotmentHeaders: [
        this.$t('nonDynamicTransport.headers.fromDate'),
        this.$t('nonDynamicTransport.headers.toDate'),
        this.$t('nonDynamicTransport.headers.releaseDate'),
        this.$t('nonDynamicTransport.headers.releaseDays'),
        this.$t('nonDynamicTransport.headers.issue'),
        this.$t('nonDynamicTransport.headers.notifications'),
        this.$t('nonDynamicTransport.headers.pnr'),
        this.$t('nonDynamicTransport.headers.info'),
        this.$t('nonDynamicTransport.headers.allotment'),
        this.$t('nonDynamicTransport.headers.netFare'),
        this.$t('nonDynamicTransport.headers.yr'),
        this.$t('nonDynamicTransport.headers.taxes'),
        this.$t('nonDynamicTransport.headers.totalFare'),
        '%',
        this.$t('nonDynamicTransport.headers.customerPrice'),
        this.$t('nonDynamicTransport.headers.infant'),
        '%',
        this.$t('nonDynamicTransport.headers.child'),
        '%',
        this.$t('nonDynamicTransport.headers.class'),
        this.$t('nonDynamicTransport.headers.cruise'),
        this.$t('nonDynamicTransport.headers.inbound'),
        this.$t('nonDynamicTransport.headers.outbound'),
      ],
      transportAllotmentsIncluded: false,
    };
  },
  created() {
    if (!this.priceIntervalSettings) {
      this.populatePriceSettings();
    } else {
      this.createDefaultPrices();
      this.setPriceObjects(
        this.priceIntervalSettings.priceSettings,
        this.priceIntervalSettings.packages,
        this.priceIntervalSettings.grossPrice,
      );
      if (!this.priceIntervalSettings.packages.length) {
        this.scanPriceSettingCategories();
      }
      this.createMissingPricesAndDiscounts();
      this.priceSettingSnapShot = cloneDeep(this.localPriceSettings);
      this.findValidParticipantsAmounts();
    }
  },
  computed: {
    ...mapState({
      suppliers: state => state.nonDynamicTransport.suppliers.items,
      syncing: state => state.itinerary.priceSettings.syncing,
      transports: state => state.nonDynamicTransport.searchTransportsPriceSetting.items,
    }),
  },
  watch: {
    priceIntervalSettings: {
      deep: true,
      handler(value) {
        if (value) {
          this.setPriceObjects(value.priceSettings, value.packages, value.grossPrice);
          this.createDefaultPrices();
          this.createMissingPricesAndDiscounts();
          this.findValidParticipantsAmounts();
        }
      },
    },
    paxHeaders: {
      immediate: true,
      handler(value) {
        this.mergeHeaders(value, ItineraryPricesTableHeaders.GENERAL_HEADERS);
        this.mergePackageHeaders(value, ItineraryPricesTableHeaders.PACKAGE_HEADERS);
        this.updatePriceSettingGroupPaxes();
      },
      deep: true,
    },
    ageDiscountPaxes(value) {
      this.ageDiscountHeaders = cloneDeep(value);
      this.updateDiscountPricePaxes(value);
    },
  },
  methods: {
    ...mapActions({
      syncItineraryPriceSettings: 'itinerary/syncItineraryPriceSettings',
      searchTransportPriceSettings: 'nonDynamicTransport/searchTransportPriceSettings',
    }),
    findValidParticipantsAmounts() {
      const numbers = {};
      const packages = [];
      const priceSettingsWithNoHotels = this.localPriceSettings.filter(
        priceSetting => priceSetting.hotels.some(hotel => !hotel.accommodation),
      );

      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels[0].setting.prices.forEach((price) => {
          if (price.price) {
            if (!numbers[`${price.paxFrom}-${price.paxTo}`]) {
              numbers[`${price.paxFrom}-${price.paxTo}`] = [];
              numbers[`${price.paxFrom}-${price.paxTo}`].push(`${price.paxFrom}-${price.paxTo}`);
            } else {
              numbers[`${price.paxFrom}-${price.paxTo}`].push(`${price.paxFrom}-${price.paxTo}`);
            }
          }
        });
      });

      this.localPackagePrices.forEach((packagePrice) => {
        packagePrice.setting.prices.forEach((price) => {
          if (price.price) {
            packages.push(price.priceKey);
          }
        });
      });

      const validNumbers = [];
      Object.keys(numbers).forEach((key) => {
        if (numbers[key].length >= this.dayProgram.length - priceSettingsWithNoHotels.length) {
          const splittedValue = numbers[key][0].split('-');
          if (splittedValue.length > 1) {
            for (let i = splittedValue[0]; i <= splittedValue[1]; i++) {
              validNumbers.push(i);
            }
          } else {
            validNumbers.push(splittedValue[0]);
          }
        }
      });

      this.validParticipantsAmounts = [...new Set(
        [...validNumbers.map(value => parseInt(value, 10)), ...packages],
      )];
    },
    setPriceObjects(priceSetting, packages, grossPrice) {
      const prices = [];
      const localSetting = cloneDeep(priceSetting);

      this.dayProgram.forEach((day) => {
        const setting = localSetting.find(e => day.id === e.dayProgramId && e.hotels);

        if (setting) {
          prices.push(setting);
        } else {
          const extraSetting = localSetting.find(e => day.id === e.dayProgramId);

          prices.push({
            ...ItineraryPricesDefaultObjects.DEFAULT_PRICE_SETTINGS,
            dayProgramId: day.id,
            description: day.description,
            hotels: [{
              ...ItineraryPricesDefaultObjects.DEFAULT_HOTEL,
              included: true,
              dayProgramId: day.id,
              category: ItineraryPricesCategories.CATEGORY_A,
              extras: [],
              setting: { ...ItineraryPricesDefaultObjects.DEFAULT_SETTING },
            }],
            extras: extraSetting ? extraSetting.extras : [],
          });
        }
      });

      this.localPriceSettings = cloneDeep(prices);
      this.localPackagePrices = cloneDeep(packages);
      this.localGrossPrice = cloneDeep(grossPrice);
    },
    createMissingPricesAndDiscounts() {
      this.localPriceSettings.forEach((priceSetting, index) => {
        if (!priceSetting.excursions) {
          priceSetting.excursions = [];
        }
        if (!priceSetting.extras) {
          priceSetting.extras = [];
        }
        if (!priceSetting.transportAllotments) {
          priceSetting.transportAllotments = [];
        }

        let newExtras = [];
        const existingExtras = [];

        this.dayProgram[index].extras.forEach((extraDayProgram) => {
          const foundExtra = priceSetting.extras.find(extra => extra.extraId === extraDayProgram.id);
          if (!foundExtra) {
            newExtras.push(extraDayProgram);
          } else {
            const indexOf = priceSetting.extras.indexOf(foundExtra);
            existingExtras.push({
              ...priceSetting.extras[indexOf],
              dayProgramId: extraDayProgram.id,
              extraId: extraDayProgram.id,
              name: extraDayProgram.description,
              category: extraDayProgram.extraCategory.toLowerCase(),
              included: extraDayProgram.type === 'included',
              setting: {
                ...priceSetting.extras[indexOf].setting,
                supplier: extraDayProgram.supplier,
              },
            });
          }
        });

        newExtras = newExtras.length
          ? this.createExtraObjects(
            newExtras,
            this.dayProgram[index],
            this.defaultPrices,
          )
          : [];

        priceSetting.extras = [...existingExtras, ...newExtras];
      });

      this.priceFiller('hotels');
      this.priceFiller('extras');

      this.localPackagePrices.forEach((packagePrice) => {
        const missingPrices = this.defaultPrices.filter(defaultPrice =>
          !packagePrice.setting.prices.find(price => price.priceKey === defaultPrice.priceKey));
        packagePrice.setting.prices.push(...missingPrices);
      });

      if (this.startDate) {
        this.localPriceSettings.forEach((priceSetting, index) => {
          priceSetting.date = this.$moment(this.startDate).add('days', index).format('YYYY-MM-DD');
        });
      }

      this.setDiscounts();
      this.setGroupPaxes();
    },
    findGroupPaxes(attribute) {
      const groupPaxes = [];
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting[attribute].forEach((attr) => {
          attr.setting.prices.forEach((price) => {
            if (price.paxFrom !== price.paxTo) {
              groupPaxes.push(price);
            }
          });
        });
      });
      return groupPaxes;
    },
    setGroupPaxes() {
      let groupPaxes = [];
      const hotelGroupPaxes = this.findGroupPaxes('hotels');
      const excursionsGroupPaxes = this.findGroupPaxes('excursions');
      const extrasGroupPaxes = this.findGroupPaxes('extras');
      groupPaxes = [...hotelGroupPaxes, ...excursionsGroupPaxes, ...extrasGroupPaxes];

      // Removes duplicates
      groupPaxes = groupPaxes.filter((obj, index, arr) =>
        arr.map(mapObj => mapObj.priceKey).indexOf(obj.priceKey) === index);

      if (groupPaxes.length) {
        this.addGroupPaxes(groupPaxes, 'hotels', false);
        this.addGroupPaxes(groupPaxes, 'excursions', false);
        this.addGroupPaxes(groupPaxes, 'extras', false);
        this.addGroupPaxes(groupPaxes, null, true);
      }

      const reformattedPaxes = [];
      groupPaxes.forEach((groupPax) => {
        reformattedPaxes.push({
          text: `${groupPax.paxFrom}-${groupPax.paxTo}`,
          priceKey: groupPax.priceKey,
        });
      });
      this.$emit('group-paxes', reformattedPaxes);
    },
    addGroupPaxes(groupPaxes, attribute, isPackage) {
      if (!isPackage) {
        groupPaxes.forEach((groupPax) => {
          this.localPriceSettings.forEach((priceSetting) => {
            if (priceSetting[attribute]) {
              priceSetting[attribute].forEach((attr) => {
                const hasPrice = attr.setting.prices.some((price => price.priceKey === groupPax.priceKey));
                if (!hasPrice) {
                  attr.setting.prices.push({
                    ...ItineraryPricesDefaultObjects.DEFAULT_PAX_PRICE,
                    priceSettingId: groupPax.priceSettingId,
                    paxFrom: groupPax.paxFrom,
                    paxTo: groupPax.paxTo,
                    priceKey: groupPax.priceKey,
                  });
                }
              });
            }
          });
        });
      } else {
        groupPaxes.forEach((groupPax) => {
          this.localPackagePrices.forEach((packagePrice) => {
            const hasPrice = packagePrice.setting.prices.some((price => price.priceKey === groupPax.priceKey));
            if (!hasPrice) {
              packagePrice.setting.prices.push({
                ...ItineraryPricesDefaultObjects.DEFAULT_PAX_PRICE,
                priceSettingId: groupPax.priceSettingId,
                paxFrom: groupPax.paxFrom,
                paxTo: groupPax.paxTo,
                priceKey: groupPax.priceKey,
              });
            }
          });
        });
      }
    },
    reformatDiscounts(discounts) {
      const reformattedDiscounts = [];
      discounts.forEach((discount) => {
        reformattedDiscounts.push({
          text: `${discount.ageFrom}-${discount.ageTo}`,
          discountKey: discount.discountKey,
        });
      });
      this.$emit('ageDiscount', reformattedDiscounts);
    },
    setDiscounts() {
      const discounts = [];
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          discounts.push(...hotel.setting.discounts);
        });
        if (priceSetting.excursions) {
          priceSetting.excursions.forEach((excursion) => {
            discounts.push(...excursion.setting.discounts);
          });
        }
        if (priceSetting.extras) {
          priceSetting.extras.forEach((extra) => {
            discounts.push(...extra.setting.discounts);
          });
        }
      });
      this.localPackagePrices.forEach((packagePrice) => {
        discounts.push(...packagePrice.setting.discounts);
      });

      const uniqueDiscounts = discounts.filter((obj, index, arr) =>
        arr.map(mapObj => mapObj.discountKey).indexOf(obj.discountKey) === index);

      if (uniqueDiscounts.length) {
        this.addDiscounts(uniqueDiscounts, 'hotels', false);
        this.addDiscounts(uniqueDiscounts, 'excursions', false);
        this.addDiscounts(uniqueDiscounts, 'extras', false);
        this.addDiscounts(uniqueDiscounts, '', true);

        this.localDiscountPricePaxes = cloneDeep(uniqueDiscounts);
        this.reformatDiscounts(uniqueDiscounts);
      }
    },
    addDiscounts(discounts, attribute, isPackage) {
      if (!isPackage) {
        discounts.forEach((discount) => {
          this.localPriceSettings.forEach((priceSetting) => {
            if (priceSetting[attribute]) {
              priceSetting[attribute].forEach((attr) => {
                const hasDiscount = attr.setting.discounts.some((attrDiscount =>
                  attrDiscount.discountKey === discount.discountKey));
                if (!hasDiscount) {
                  const { priceSettingId, ageFrom, ageTo, discountKey } = discount;
                  attr.setting.discounts.push({
                    ...ItineraryPricesDefaultObjects.DEFAULT_DISCOUNT_PRICE,
                    priceSettingId,
                    ageFrom,
                    ageTo,
                    discountKey,
                  });
                }
              });
            }
          });
        });
      } else {
        discounts.forEach((discount) => {
          this.localPackagePrices.forEach((packagePrice) => {
            const hasDiscount = packagePrice.setting.discounts.some((attrDiscount =>
              attrDiscount.discountKey === discount.discountKey));
            if (!hasDiscount) {
              const { priceSettingId, ageFrom, ageTo, discountKey } = discount;
              packagePrice.setting.discounts.push({
                ...ItineraryPricesDefaultObjects.DEFAULT_DISCOUNT_PRICE,
                priceSettingId,
                ageFrom,
                ageTo,
                discountKey,
              });
            }
          });
        });
      }
    },
    priceFiller(attribute) {
      this.localPriceSettings.forEach((priceSetting) => {
        if (priceSetting[attribute]) {
          priceSetting[attribute].forEach((attr) => {
            const missingPrices = this.defaultPrices.filter(defaultPrice =>
              !attr.setting.prices.find(price => price.priceKey === defaultPrice.priceKey));
            attr.setting.prices.push(...missingPrices);
          });
        }
      });
    },
    createDefaultPrices() {
      const defaultPrices = [];
      for (let i = 0; i < 9; i++) {
        const age = i + 1;
        defaultPrices.push({
          ...ItineraryPricesDefaultObjects.DEFAULT_PAX_PRICE,
          paxFrom: age,
          paxTo: age,
          priceKey: age,
        });
      }
      this.defaultPrices = [...defaultPrices];
    },
    populatePriceSettings() {
      this.createDefaultPrices();
      this.dayProgram.forEach((value, index) => {
        let date = null;

        if (this.startDate) {
          date = this.$moment(this.startDate).add('days', index).format('YYYY-MM-DD');
        }

        const extras = this.createExtraObjects(value.extras, value, this.defaultPrices);
        this.localPriceSettings.push({
          ...ItineraryPricesDefaultObjects.DEFAULT_PRICE_SETTINGS,
          dayProgramId: value.id,
          date,
          hotels: [{
            ...ItineraryPricesDefaultObjects.DEFAULT_HOTEL,
            included: true,
            dayProgramId: value.id,
            category: ItineraryPricesCategories.CATEGORY_A,
          }],
          extras: [...extras],
        });
      });

      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          hotel.setting = {
            ...ItineraryPricesDefaultObjects.DEFAULT_SETTING,
            prices: [...this.defaultPrices],
          };
        });
      });
      this.createPricePackage(this.defaultPrices, ItineraryPricesCategories.CATEGORY_A);
    },
    createExtraObjects(extras, dayProgram, defaultPrices) {
      if (extras.length) {
        const localExtras = [];
        extras.forEach((extra) => {
          const extraPriceSetting = cloneDeep({
            ...ItineraryPricesDefaultObjects.DEFAULT_EXTRA,
            dayProgramId: dayProgram.id,
            extraId: extra.id,
            name: extra.description,
            category: extra.extraCategory.toLowerCase(),
            included: extra.type === 'included',
            setting: {
              ...ItineraryPricesDefaultObjects.DEFAULT_SETTING,
              supplier: extra.supplier,
              prices: [...defaultPrices],
            },
          });
          localExtras.push(extraPriceSetting);
        });
        return localExtras;
      }
      return [];
    },
    updatePriceSettingGroupPaxes() {
      const groupPaxes = [];
      this.paxHeaders.forEach((paxHeader) => {
        const paxValues = paxHeader.text.split('-');
        if (paxValues.length > 1) {
          groupPaxes.push({
            ...ItineraryPricesDefaultObjects.DEFAULT_PAX_PRICE,
            paxFrom: paxValues[0],
            paxTo: paxValues[1],
            priceKey: paxHeader.priceKey,
          });
        }
      });

      if (groupPaxes.length) {
        if (!this.localGroupPaxes.length) {
          this.populatePriceSettingGroupPrices(groupPaxes);
        } else {
          const newGroupPaxes = this.priceComparer(groupPaxes, this.localGroupPaxes);
          const deletedGroupPaxes = this.priceComparer(this.localGroupPaxes, groupPaxes);
          this.groupPriceUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes, 'hotels');
          this.groupPriceUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes, 'excursions');
          this.groupPriceUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes, 'extras');
          this.groupPricePackageUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes);
        }
        this.localGroupPaxes = [...groupPaxes];
      }
    },
    createPricePackage(defaultPrices, category) {
      this.localPackagePrices.push(cloneDeep({
        ...ItineraryPricesDefaultObjects.DEFAULT_PACKAGE_PRICE,
        category,
        setting: {
          ...ItineraryPricesDefaultObjects.DEFAULT_SETTING,
          prices: defaultPrices,
        },
      }));
    },
    scanPriceSettingCategories() {
      const packageA = [];
      const packageB = [];
      const packageC = [];

      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          if (hotel.category === ItineraryPricesCategories.CATEGORY_A) { packageA.push(true); }
          if (hotel.category === ItineraryPricesCategories.CATEGORY_B) { packageB.push(true); }
          if (hotel.category === ItineraryPricesCategories.CATEGORY_C) { packageC.push(true); }
        });
      });

      this.checkPackage(packageA, ItineraryPricesCategories.CATEGORY_A);
      this.checkPackage(packageB, ItineraryPricesCategories.CATEGORY_B);
      this.checkPackage(packageC, ItineraryPricesCategories.CATEGORY_C);
    },
    checkPackage(selectedPackage, category) {
      // Some price settings are a flight day, those should not be included in the comparison.
      const filteredPriceSettings = this.localPriceSettings.filter(priceSetting =>
        priceSetting.hotels.filter(hotel => hotel.accommodationSiteId));
      if (selectedPackage.length >= filteredPriceSettings.length) {
        if (!this.localPackagePrices.some(packagePrice => packagePrice.category === category)) {
          this.createPricePackage(this.defaultPrices, category);
        }
      } else {
        const packageIndex = this.localPackagePrices.map(e => e.category).indexOf(category);
        if (packageIndex >= 0) {
          const localPackagePrices = [...this.localPackagePrices];
          localPackagePrices.splice(packageIndex, 1);
          this.localPackagePrices = [...localPackagePrices];
        }
      }
    },
    mergeHeaders(customHeaders, defaultHeaders) {
      const headers = cloneDeep(defaultHeaders);
      headers.splice(6, 0, ...customHeaders);
      this.mergedHeaders = cloneDeep(headers);
    },
    mergePackageHeaders(customHeaders, defaultHeaders) {
      const headers = cloneDeep(defaultHeaders);
      headers.splice(6, 0, ...customHeaders);
      this.mergedPackageHeaders = cloneDeep(headers);
    },
    toggleAgeDiscountModal(value) {
      this.selectedAgeDiscount = value;
      this.ageDiscountModal = !this.ageDiscountModal;
    },
    updateDiscountPricePaxes(discountPaxes) {
      const discountPaxObjects = [];
      discountPaxes.forEach((discountPax) => {
        const ages = discountPax.text.split('-');
        discountPaxObjects.push({
          ...ItineraryPricesDefaultObjects.DEFAULT_DISCOUNT_PRICE,
          ageFrom: ages[0],
          ageTo: ages[1],
          discountKey: discountPax.discountKey,
        });
      });
      if (!this.localDiscountPricePaxes.length) {
        this.populatePriceSettingDiscounts(discountPaxObjects);
      } else if (JSON.stringify(discountPaxObjects) !== JSON.stringify(this.localDiscountPricePaxes)) {
        this.updatePriceSettingDiscounts(discountPaxObjects);
      }

      this.localDiscountPricePaxes = cloneDeep(discountPaxObjects);
    },
    populatePriceSettingDiscounts(discounts) {
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          hotel.setting.discounts = [...discounts];
        });
        priceSetting.excursions.forEach((excursion) => {
          excursion.setting.discounts = [...discounts];
        });
      });
      this.localPackagePrices.forEach((packagePrice) => {
        packagePrice.setting.discounts = [...discounts];
      });
    },
    populatePriceSettingGroupPrices(groupPrices) {
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          hotel.setting.prices.push(...groupPrices);
        });
        priceSetting.excursions.forEach((excursion) => {
          excursion.setting.prices.push(...groupPrices);
        });
        priceSetting.extras.forEach((extra) => {
          extra.setting.prices.push(...groupPrices);
        });
      });
      this.localPackagePrices.forEach((packagePrice) => {
        packagePrice.setting.prices.push(...groupPrices);
      });
    },
    updatePriceSettingDiscounts(discounts) {
      const newDiscounts = this.discountComparer(discounts, this.localDiscountPricePaxes);
      const deletedDiscounts = this.discountComparer(this.localDiscountPricePaxes, discounts);
      this.discountUpdate(discounts, newDiscounts, deletedDiscounts, 'hotels');
      this.discountUpdate(discounts, newDiscounts, deletedDiscounts, 'excursions');
      this.discountUpdate(discounts, newDiscounts, deletedDiscounts, 'extras');
      this.discountPackageUpdater(discounts, newDiscounts, deletedDiscounts);
    },
    discountComparer(newDiscounts, oldDiscounts) {
      return newDiscounts.filter(({ discountKey: key1 }) =>
        !oldDiscounts.some(({ discountKey: key2 }) => key2 === key1));
    },
    priceComparer(newPrices, oldPrices) {
      return newPrices.filter(({ priceKey: key1 }) =>
        !oldPrices.some(({ priceKey: key2 }) => key2 === key1));
    },
    groupPriceUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes, attribute) {
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting[attribute].forEach((attr) => {
          groupPaxes.forEach((groupPax) => {
            attr.setting.prices.forEach((price) => {
              if (groupPax.priceKey === price.priceKey) {
                price.paxFrom = groupPax.paxFrom;
                price.paxTo = groupPax.paxTo;
              }
            });
          });
          attr.setting.prices.push(...newGroupPaxes);
          deletedGroupPaxes.forEach((deletedGroupPax) => {
            const index = attr.setting.discounts.map(e => e.priceKey).indexOf(deletedGroupPax.priceKey);
            const localPrices = [...attr.setting.prices];
            localPrices.splice(index, 1);
            attr.setting.prices = [...localPrices];
          });
        });
      });
    },
    groupPricePackageUpdater(groupPaxes, newGroupPaxes, deletedGroupPaxes) {
      this.localPackagePrices.forEach((packagePrice) => {
        groupPaxes.forEach((groupPax) => {
          packagePrice.setting.prices.forEach((price) => {
            if (groupPax.priceKey === price.priceKey) {
              price.paxFrom = groupPax.paxFrom;
              price.paxTo = groupPax.paxTo;
            }
          });
        });
        packagePrice.setting.prices.push(...newGroupPaxes);
        deletedGroupPaxes.forEach((deletedGroupPax) => {
          const index = packagePrice.setting.prices.map(e => e.priceKey).indexOf(deletedGroupPax.priceKey);
          const localPrices = [...packagePrice.setting.prices];
          localPrices.splice(index, 1);
          packagePrice.setting.prices = [...localPrices];
        });
      });
    },
    discountUpdate(discounts, newDiscounts, deletedDiscounts, attribute) {
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting[attribute].forEach((attr) => {
          discounts.forEach((discount) => {
            attr.setting.discounts.forEach((hotelDiscount) => {
              if (discount.discountKey === hotelDiscount.discountKey) {
                hotelDiscount.ageFrom = discount.ageFrom;
                hotelDiscount.ageTo = discount.ageTo;
              }
            });
          });
          attr.setting.discounts.push(...newDiscounts);
          deletedDiscounts.forEach((deleteDiscount) => {
            const index = attr.setting.discounts.map(e => e.discountKey).indexOf(deleteDiscount.discountKey);
            const localDiscounts = [...attr.setting.discounts];
            localDiscounts.splice(index, 1);
            attr.setting.discounts = [...localDiscounts];
          });
        });
      });
    },
    discountPackageUpdater(discounts, newDiscounts, deletedDiscounts) {
      this.localPackagePrices.forEach((packagePrice) => {
        discounts.forEach((discount) => {
          packagePrice.setting.discounts.forEach((packageDiscount) => {
            if (discount.discountKey === packageDiscount.discountKey) {
              packageDiscount.ageFrom = discount.ageFrom;
              packageDiscount.ageTo = discount.ageTo;
            }
          });
        });
        packagePrice.setting.discounts.push(...newDiscounts);
        deletedDiscounts.forEach((deleteDiscount) => {
          const index = packagePrice.setting.discounts.map(e => e.discountKey).indexOf(deleteDiscount.discountKey);
          const localDiscounts = [...packagePrice.setting.discounts];
          localDiscounts.splice(index, 1);
          packagePrice.setting.discounts = [...localDiscounts];
        });
      });
    },
    addHotel(index) {
      const areaId = this.dayProgram[index].areaId;

      const dayProgramKeys = this.dayProgram.map((program, programIndex) => {
        if (program.areaId === areaId) {
          return programIndex;
        }
        return null;
      });

      const hotel = {
        ...ItineraryPricesDefaultObjects.DEFAULT_HOTEL,
        setting: cloneDeep({
          ...ItineraryPricesDefaultObjects.DEFAULT_SETTING,
          prices: [...this.defaultPrices],
        }),
      };

      if (this.localDiscountPricePaxes.length) {
        hotel.setting.discounts = [...this.localDiscountPricePaxes];
      }

      dayProgramKeys.forEach((dayProgramKey) => {
        if (this.localPriceSettings[dayProgramKey] && this.dayProgram[dayProgramKey].hotels.length) {
          this.localPriceSettings[dayProgramKey].hotels.push(hotel);
        }
      });

      this.setGroupPaxes();
      this.setDiscounts();
    },
    updateHotel(localPriceSettingIndex, hotelIndex, hotel) {
      this.localPriceSettings[localPriceSettingIndex].hotels[hotelIndex] = hotel;
      this.scanPriceSettingCategories();
      this.findValidParticipantsAmounts();
    },
    deleteHotel(localPriceSettingIndex, hotelIndex) {
      const hotels = this.localPriceSettings[localPriceSettingIndex].hotels;
      hotels.splice(hotelIndex, 1);
      this.localPriceSettings[localPriceSettingIndex].hotels = [...hotels];
    },
    deleteExtra(priceSettingIndex, extraIndex) {
      const priceSetting = [...this.localPriceSettings];
      priceSetting[priceSettingIndex].extras.splice(extraIndex, 1);
      this.localPriceSettings = priceSetting;
    },
    updateExtra(priceSettingIndex, extraIndex, extra) {
      this.localPriceSettings[priceSettingIndex].extras[extraIndex] = extra;
    },
    updatePackagePrice(packagePriceIndex, packagePrice) {
      this.localPackagePrices[packagePriceIndex] = packagePrice;
      const packageCategory = this.localPackagePrices[packagePriceIndex].category;
      const packageSupplier = this.localPackagePrices[packagePriceIndex].setting.supplier;
      if (packageSupplier) {
        this.updateHotelSupplierByPackage(packagePriceIndex, packageCategory, packageSupplier);
      }
      this.findValidParticipantsAmounts();
    },
    updateHotelSupplierByPackage(packagePriceIndex, packageCategory, packageSupplier) {
      this.localPriceSettings.forEach((priceSetting) => {
        priceSetting.hotels.forEach((hotel) => {
          const noPrice = hotel.setting.prices.every(price => price.price === null);
          if (hotel.category === packageCategory && noPrice) {
            hotel.setting.supplier = cloneDeep(packageSupplier);
          }
        });
      });
    },
    setHotelDiscounts(priceSettingIndex, value) {
      const priceSettings = [...this.localPriceSettings];
      const selectedSite = priceSettings[priceSettingIndex].hotels[value.siteIndex];

      // Find all localPriceSettings with same supplier and site

      priceSettings.forEach((priceSetting) => {
        if (priceSetting.hotels[value.siteIndex]
          && priceSetting.hotels[value.siteIndex].setting.supplier
          && selectedSite.setting.supplier
        ) {
          const sameSupplier = selectedSite.setting.supplier.id
            === priceSetting.hotels[value.siteIndex].setting.supplier.id;
          const sameSite = selectedSite.accommodationSiteId
            === priceSetting.hotels[value.siteIndex].accommodationSiteId;
          if (sameSupplier && sameSite) {
            priceSetting.hotels[value.siteIndex].setting.discounts = value.discounts;
          }
        }
      });

      this.localPriceSettings = [...priceSettings];
    },
    setPackageDiscounts(value) {
      this.localPackagePrices[value.index].setting.discounts = value.discounts;
    },
    resetSupplier(priceSettingIndex, hotelIndex) {
      const priceSettingCategory = this.localPriceSettings[priceSettingIndex].hotels[hotelIndex].setting.category;
      this.localPackagePrices.forEach((packagePrice) => {
        if (priceSettingCategory === packagePrice.category) {
          this.localPriceSettings[priceSettingIndex].hotels[hotelIndex].setting.supplier =
            packagePrice.setting.supplier;
        }
      });
    },
    syncPriceSettings(grossPrice) {
      const priceIntervalId = this.selectedPriceInterval.id;
      this.removeNullValues('hotels', 'prices', false);
      this.removeNullValues('hotels', 'discounts', false);
      this.removeNullValues('extras', 'discounts', false);
      this.removeNullValues('extras', 'prices', false);
      // Removes from packages
      this.removeNullValues(null, 'prices', 'price', true);
      this.removeNullValues(null, 'discounts', true);

      // Remove days with no hotels
      const localPriceSettings = cloneDeep(this.localPriceSettings);

      const priceSettingsWithHotels = localPriceSettings.map((priceSetting) => {
        const hotels = priceSetting.hotels.filter(hotel => hotel.accommodationSiteId);
        return {
          ...priceSetting,
          hotels,
        };
      });

      const packagesToRemove = [];
      this.localPackagePrices.forEach((packagePrice) => {
        const hasPrices = packagePrice.setting.prices.some(e => e.price);
        if (!hasPrices) {
          packagesToRemove.push(packagePrice.category);
        }
      });

      const packagePrices = cloneDeep(this.localPackagePrices);
      const filteredPackages = packagePrices.filter(e => !packagesToRemove.includes(e.category));

      const payload = {
        priceSettings: priceSettingsWithHotels,
        packages: filteredPackages,
        grossPrice,
        priceIntervalId,
      };

      this.syncItineraryPriceSettings(payload);
      this.createMissingPricesAndDiscounts();
    },
    removeNullValues(attribute, field, isPackage) {
      const value = field.slice(0, -1);
      if (!isPackage) {
        this.localPriceSettings.forEach((priceSetting) => {
          if (priceSetting[attribute]) {
            priceSetting[attribute].forEach((attr) => {
              const newPrices = attr.setting[field].filter(val => val[value] !== null);
              attr.setting[field] = [...newPrices];
            });
          }
        });
      } else {
        this.localPackagePrices.forEach((packagePrice) => {
          const newPrices = packagePrice.setting[field].filter(val => val[value] !== null);
          packagePrice.setting[field] = [...newPrices];
        });
      }
    },
    autoSetSite(priceSettingIndex, hotelIndex, site) {
      const areaId = this.dayProgram[priceSettingIndex].areaId;
      const indexes = this.dayProgram.map((program, index) => {
        if (program.areaId === areaId && program.hotelCount) {
          return index;
        }
        return null;
      });
      this.localPriceSettings.forEach((priceSetting, index) => {
        if (indexes.includes(index)) {
          const hotelFound = this.dayProgram[index].hotels.find(
            priceSettingHotel => priceSettingHotel.siteId === site.hotel.siteId
          );
          if (typeof hotelFound !== 'undefined') {
            priceSetting.hotels[hotelIndex].site = site.hotel;
            priceSetting.hotels[hotelIndex].accommodation = site.room;
            priceSetting.hotels[hotelIndex].setting.supplier = site.room.supplier;
            priceSetting.hotels[hotelIndex].accommodationSiteId = site.room.accommodationSiteId;
          }
        }
      });
    },
    searchTransports(searchBody) {
      this.searchTransportPriceSettings(searchBody);
    },
    addTransportAllotment(index, transportAllotment) {
      const priceSettings = cloneDeep(this.localPriceSettings);
      priceSettings[index].transportAllotments.push({
        dayProgramId: this.dayProgram[index].id,
        priceIntervalId: null,
        included: this.transportAllotmentsIncluded,
        id: transportAllotment.id,
        allotment: transportAllotment,
      });
      this.localPriceSettings = priceSettings;
    },
    removeTransportAllotment(index, transportAllotment) {
      const priceSettings = [...this.localPriceSettings];
      const removeIndex = priceSettings[index].transportAllotments.findIndex(allotment =>
        allotment.id === transportAllotment.id);
      priceSettings[index].transportAllotments.splice(removeIndex, 1);
      this.localPriceSettings = priceSettings;
    },
    setIncluded(value) {
      this.transportAllotmentsIncluded = value;
    },
  },
};
</script>

<template>
  <v-layout column>
    <div class="wrapper">
      <v-data-table
        :headers="mergedHeaders"
        :items="localPriceSettings"
        item-key="dayProgramId"
        expand
        hide-actions
        disable-initial-sort
        class="fixed-table ma-0 pa-0"
      >
        <template v-slot:headers="props">
          <tr>
            <th
              :key="index"
              :style="{ 'width': `${header.width}px` }"
              v-for="(header, index) in props.headers"
              class="text-xs-left ma-0 pa-0"
            >
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <itinerary-prices-price-settings-table-row
            :props="props"
            :setting="props.item.hotels[0]"
            :index="props.index"
            :day="props.index"
            :date="props.item.date"
            :pax-headers="paxHeaders"
            :age-discount-paxes="ageDiscountPaxes"
            :availableHotels="dayProgram[props.index].hotels"
            :suppliers="suppliers"
            :price-settings="localPriceSettings[props.index]"
            @update="updateHotel(props.index, 0, $event)"
            @discountModal="toggleAgeDiscountModal(props.index)"
            @setDiscounts="setHotelDiscounts(props.index, $event)"
            @no-prices="resetSupplier(props.index, 0)"
            @auto-set-site="autoSetSite(props.index, 0, $event)"
          />
        </template>
        <template v-slot:expand="props">
          <v-card class="expanded-wrapper">
            <h3 v-if="localPriceSettings[props.index].hotels.length > 1">Extra hotels:</h3>
            <v-data-table
              v-if="localPriceSettings[props.index].hotels.length > 1"
              :headers="mergedHeaders"
              :items="props.item.hotels"
              hide-actions
              disable-initial-sort
              class="fixed-table"
            >
              <template v-slot:headers="props">
                <tr class="expanded-table-row">
                  <th
                    v-for="(header, index) in props.headers"
                    :key="index"
                    :style="{ 'width': `${header.width}px` }"
                    class="hide-table-header text-xs-left ma-0 pa-0"
                  >
                    {{ header.text }}
                  </th>
                </tr>
              </template>
              <template v-slot:items="prop">
                <itinerary-prices-price-settings-table-row
                  v-if="prop.index !== 0"
                  :setting="prop.item"
                  :index="prop.index"
                  :hotel-index="prop.index"
                  :day="props.index"
                  :date="localPriceSettings[props.index].date"
                  :pax-headers="paxHeaders"
                  :availableHotels="dayProgram[props.index].hotels"
                  :suppliers="suppliers"
                  :price-settings="localPriceSettings[props.index]"
                  extended-row
                  @update="updateHotel(props.index, prop.index, $event)"
                  @delete="deleteHotel(props.index, $event)"
                  @setDiscounts="setHotelDiscounts(props.index, $event)"
                  @no-prices="resetSupplier(props.index, prop.index)"
                  @auto-set-site="autoSetSite(props.index, prop.index, $event)"
                />
              </template>
            </v-data-table>

            <template v-if="props.item.extras.some(extra => extra.category.toLowerCase() === 'excursion')">
              <h3>Excursions:</h3>
              <v-data-table
                :headers="mergedHeaders"
                :items="props.item.extras"
                hide-actions
                disable-initial-sort
              >
                <template v-slot:headers="props">
                  <tr class="expanded-table-row">
                    <th
                      v-for="(header, index) in props.headers"
                      :key="index"
                      :style="{ 'width': `${header.width}px` }"
                      class="hide-table-header text-xs-left ma-0 pa-0"
                    >
                      {{ header.text }}
                    </th>
                  </tr>
                </template>
                <template v-slot:items="prop">
                  <itinerary-prices-price-settings-table-row
                    v-if="prop.item.category.toLowerCase() === 'excursion'"
                    :setting="prop.item"
                    :index="prop.index"
                    :date="localPriceSettings[props.index].date"
                    :pax-headers="paxHeaders"
                    :suppliers="suppliers"
                    extra-row
                    extended-row
                    @update="updateExtra(props.index, prop.index, $event)"
                  />
                </template>
              </v-data-table>
            </template>

            <template v-if="props.item.extras.some(extra => extra.category.toLowerCase() === 'transfer')">
              <h3>Transfers:</h3>
              <v-data-table
                :headers="mergedHeaders"
                :items="props.item.extras"
                hide-actions
                disable-initial-sort
              >
                <template v-slot:headers="props">
                  <tr class="expanded-table-row">
                    <th
                      :key="index"
                      :style="{ 'width': `${header.width}px` }"
                      v-for="(header, index) in props.headers"
                      class="hide-table-header text-xs-left ma-0 pa-0"
                    >
                      {{ header.text }}
                    </th>
                  </tr>
                </template>
                <template v-slot:items="prop">
                  <itinerary-prices-price-settings-table-row
                    v-if="prop.item.category.toLowerCase() === 'transfer'"
                    :setting="prop.item"
                    :index="prop.index"
                    :pax-headers="paxHeaders"
                    :suppliers="suppliers"
                    extended-row
                    extra-row
                    @update="updateExtra(props.index, prop.index, $event)"
                    @delete="deleteExtra(props.index, $event)"
                  />
                </template>
              </v-data-table>
            </template>

            <v-layout column>
              <v-flex row mt-4>
                <v-btn color="primary" @click="addHotel(props.index)">
                  Add hotel
                </v-btn>
              </v-flex>

              <itinerary-prices-transport-search-bar
                :selected-price-interval="selectedPriceInterval"
                :itinerary-length="dayProgram.length"
                :carriers="carriers"
                :transits="transits"
                expanded
                @included="setIncluded"
                @search="searchTransports"
              />
              <itinerary-prices-transport-search-feed
                :transport-search-results="transports"
                :selected-allotments="props.item.transportAllotments"
                @save="setTransportAllotments(props.index, $event)"
                @add-transport="addTransportAllotment(props.index, $event)"
                @remove-transport="removeTransportAllotment(props.index, $event)"
              />
              <table v-if="props.item.transportAllotments.length" class="selected-allotments-table mt-2 mb-4">
                <tr>
                  <th
                    v-for="(header, index) in transportAllotmentHeaders"
                    :key="`table-header-${index}`"
                    class="table-header"
                  >
                    {{ header }}
                  </th>
                </tr>
                <non-dynamic-transport-allotment-row
                  v-for="(allotment, transportAllotmentIndex) in props.item.transportAllotments"
                  :key="transportAllotmentIndex"
                  :allotment="allotment.allotment"
                  :index="allotment.id"
                  read-only
                  @remove="removeTransportAllotment(props.index, transportAllotmentIndex)"
                />
              </table>
            </v-layout>
            <v-divider />
          </v-card>
        </template>
      </v-data-table>
      <v-data-table
        :headers="mergedPackageHeaders"
        :items="localPackagePrices"
        hide-actions
        disable-initial-sort
        class="fixed-table"
      >
        <template v-slot:headers="props">
          <tr>
            <th
              v-for="(header, index) in props.headers"
              :key="index"
              :style="{ 'width': `${header.width}px` }"
              class="text-xs-left ma-0 pa-0"
            >
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <itinerary-prices-price-settings-table-row
            :setting="props.item"
            :index="props.index"
            :pax-headers="paxHeaders"
            :suppliers="suppliers"
            package-row
            @update="updatePackagePrice(props.index, $event)"
            @setDiscounts="setPackageDiscounts"
          />
        </template>
      </v-data-table>
    </div>
    <v-divider class="mt-3" />
    <div class="mt-3">
      <itinerary-prices-price-selector
        :gross-price="localGrossPrice"
        :price-setting="localPriceSettings[0]"
        :valid-participants-amounts="validParticipantsAmounts"
        :syncing="syncing"
        @sync="syncPriceSettings"
      />
    </div>
  </v-layout>
</template>

<style lang="scss">
  .fixed-table .v-table {
    table-layout: fixed!important;

    th {
      white-space: unset;
    }
  }
</style>

<style scoped lang="scss">
  @import '../../../assets/scss/variables';

  .wrapper {
    width: 100%;
  }

  .expanded-wrapper {
    position: relative;
    top: -8px;
  }

  .expanded-table-row {
    height: 0;
  }

  .v-table tr:hover:not(.v-table__expanded__content) {
    background: unset !important;
  }

  .hide-table-header {
    height: 0;
    color: white !important;
    opacity: 0;
  }

  .selected-allotments-table {
    width: 100%;
    font-size: 12px;

    th {
      font-size: $font-size-sm;
      height: unset!important;
      padding: 0 0!important;
      margin: 0 0!important;
    }
  }

  h3 {
    position: relative;
    top: $spacing-md;
    padding-left: $spacing-sm;
    margin-top: 8px;
    font-weight: 500;
    color: #0C3744;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesPriceSettingsTable.vue